import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';
import { CREATE_PROJECT, UPDATE_PROJECT } from '../../graphql/mutations';
import { GET_PROJECTS, GET_PROJECT } from '../../graphql/queries';
import { useAccount } from '../../context/AccountContext';
import { toast } from 'sonner';

interface Project {
  id: string;
  name: string;
  plan?: string;
  deadline: string;
  campaignStory?: string;
  landingPageUrl?: string;
}

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  project?: Project | null; // If provided, modal is in edit mode
  onSubmit?: (project: {
    name: string;
    plan: string;
    deadline: Date | undefined;
    campaignStory: string;
    landingPageUrl: string;
  }) => void;
}

export function CreateProjectModal({ open, onClose, project, onSubmit }: CreateProjectModalProps) {
  const isEditMode = !!project;
  const [name, setName] = useState('');
  const [plan, setPlan] = useState('');
  const [deadline, setDeadline] = useState<Date>();
  const [campaignStory, setCampaignStory] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');

  const { accountId } = useAccount();

  // Populate fields when in edit mode
  useEffect(() => {
    if (project && open) {
      setName(project.name || '');
      setPlan(project.plan || '');
      setDeadline(project.deadline ? new Date(project.deadline) : undefined);
      setCampaignStory(project.campaignStory || '');
      setLandingPageUrl(project.landingPageUrl || '');
    } else if (!project && open) {
      // Reset fields when creating new project
      setName('');
      setPlan('');
      setDeadline(undefined);
      setCampaignStory('');
      setLandingPageUrl('');
    }
  }, [project, open]);

  const [createProject, { loading: creating }] = useMutation(CREATE_PROJECT, {
    refetchQueries: [{ query: GET_PROJECTS, variables: { accountId } }],
    onCompleted: () => {
      toast.success('Project created successfully!');
      handleClose();
      if (onSubmit) {
        onSubmit({
          name,
          plan,
          deadline,
          campaignStory,
          landingPageUrl,
        });
      }
    },
    onError: (error) => {
      toast.error(`Failed to create project: ${error.message}`);
    },
  });

  const [updateProject, { loading: updating }] = useMutation(UPDATE_PROJECT, {
    refetchQueries: [
      { query: GET_PROJECTS, variables: { accountId } },
      { query: GET_PROJECT, variables: { id: project?.id } },
    ],
    onCompleted: () => {
      toast.success('Project updated successfully!');
      handleClose();
      if (onSubmit) {
        onSubmit({
          name,
          plan,
          deadline,
          campaignStory,
          landingPageUrl,
        });
      }
    },
    onError: (error) => {
      toast.error(`Failed to update project: ${error.message}`);
    },
  });

  const loading = creating || updating;

  const handleSubmit = () => {
    if (!deadline) return;

    if (isEditMode && project) {
      // Update existing project
      updateProject({
        variables: {
          id: project.id,
          input: {
            name,
            plan: plan || undefined,
            deadline: deadline.toISOString(),
            campaignStory: campaignStory || undefined,
            landingPageUrl: landingPageUrl || undefined,
          },
        },
      });
    } else {
      // Create new project
      if (!accountId) return;
      createProject({
        variables: {
          input: {
            accountId,
            name,
            plan: plan || undefined,
            deadline: deadline.toISOString(),
            status: 'planning',
            progress: 0,
            campaignStory: campaignStory || undefined,
            landingPageUrl: landingPageUrl || undefined,
          },
        },
      });
    }
  };

  const handleClose = () => {
    setName('');
    setPlan('');
    setDeadline(undefined);
    setCampaignStory('');
    setLandingPageUrl('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-core-dark text-white">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Project' : 'Create New Project'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update your project details. You can modify the name, brief, deadline, and other settings.'
              : 'Set up a new social media project. You can add posts and team members later.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Project Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Summer Product Launch"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-form-dark text-cmp-ink"
            />
          </div>

          {/* Project Brief */}
          <div className="space-y-2">
            <Label htmlFor="campaignStory">Project Brief *</Label>
            <Textarea
              id="campaignStory"
              placeholder="Describe the main narrative, goals, and key messages of this campaign..."
              value={campaignStory}
              onChange={(e) => setCampaignStory(e.target.value)}
              className="min-h-32 resize-none bg-form-dark text-cmp-ink"
            />
            <p className="descriptor-text">
              This helps your team understand the campaign's objectives and messaging.
            </p>
          </div>

          {/* Deadline */}
          <div className="space-y-2">
            <Label>Project Deadline *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal bg-form-dark text-cmp-ink"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {deadline ? format(deadline, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="!w-fit p-2 bg-core-dark" align="start">
                <Calendar
                  mode="single"
                  selected={deadline}
                  onSelect={setDeadline}
                  initialFocus
                  className="p-2"
                  classNames={{
                    cell: 'flex-shrink-0 w-9 px-0.5',
                    day: 'flex-shrink-0 w-9 h-9',
                    head_cell: 'flex-shrink-0 w-9 px-0.5',
                    row: 'gap-0.5',
                    table: 'gap-0',
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Landing Page URL */}
          <div className="space-y-2">
            <Label htmlFor="landingPage">Landing Page URL (Optional)</Label>
            <Input
              id="landingPage"
              type="url"
              placeholder="https://example.com/campaign"
              value={landingPageUrl}
              onChange={(e) => setLandingPageUrl(e.target.value)}
              className="bg-form-dark text-cmp-ink"
            />
            <p className="descriptor-text">
              Link to the landing page associated with this campaign.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!name || !campaignStory || !deadline || loading}>
            {loading
              ? isEditMode
                ? 'Updating...'
                : 'Creating...'
              : isEditMode
                ? 'Update Project'
                : 'Create Project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
